import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useActiveFamily } from '../context/ActiveFamilyContext';

export interface PlanLimits {
  families: number;
  members: number;
  admins: number;
  activities: number;
  checklists: number;
  rewards: number;
  recipes: number;
  mealPlans: number;
  shoppingLists: number;
  familyCalls?: number;
  evaluations?: number;
  subjects?: number;
  groups?: number;
}

// Fallback genérico cuando no se puede leer de Firestore
// NO contiene valores hardcodeados específicos - todo bloqueado por defecto
const FALLBACK_LIMITS: PlanLimits = {
  families: 1,
  members: 1,
  admins: 1,
  activities: -1,
  checklists: -1,
  rewards: -1,
  recipes: -1,
  mealPlans: -1,
  shoppingLists: -1,
  familyCalls: -1,
  evaluations: -1,
  subjects: -1,
  groups: -1,
};

const FALLBACK_LIMITS_BY_PLAN: Record<'free' | 'familiar' | 'enterprise', PlanLimits> = {
  free: FALLBACK_LIMITS,
  familiar: {
    families: 3,
    members: 14,
    admins: 6,
    activities: 0,
    checklists: 0,
    rewards: 10,
    recipes: 0,
    mealPlans: 0,
    shoppingLists: 0,
    familyCalls: 0,
    evaluations: 0,
    subjects: 0,
    groups: 0,
    aiAssistantConversationsPerMonth: 30,
  },
  enterprise: {
    families: 9999,
    members: 9999,
    admins: 9999,
    activities: 0,
    checklists: 0,
    rewards: 0,
    recipes: 0,
    mealPlans: 0,
    shoppingLists: 0,
    familyCalls: 0,
    evaluations: 0,
    subjects: 0,
    groups: 0,
    aiAssistantConversationsPerMonth: -1,
  },
};

function normalizePlanId(raw: unknown): 'free' | 'familiar' | 'enterprise' {
  const v = String(raw || '').toLowerCase().trim();
  if (v === 'enterprise' || v === 'premium') return 'enterprise';
  if (v === 'family' || v === 'familiar') return 'familiar';
  return 'free';
}

export interface PlanInfo {
  id: string;
  name: string;
  limits: PlanLimits;
  features: string[];
  isActive: boolean;
}

/**
 * Hook para verificar límites del plan actual
 * Retorna información sobre el plan activo y funciones para verificar límites
 */
export function usePlanLimits() {
  const { activeFamily, loading: activeFamilyLoading } = useActiveFamily();
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const [isTrialActiveState, setIsTrialActiveState] = useState(false);

  useEffect(() => {
    async function loadPlan() {
      if (activeFamilyLoading) {
        setLoading(true);
        return;
      }

      if (!activeFamily?.id || !activeFamily?.ownerId) {
        setIsTrialActiveState(false);
        // Sin familia activa - intentar leer plan FREE desde Firestore
        try {
          const freePlanDoc = await getDoc(doc(db, 'subscriptionPlans', 'free'));
          if (freePlanDoc.exists()) {
            const freePlanData = freePlanDoc.data();
            setPlanInfo({
              id: 'free',
              name: freePlanData.name || 'Free',
              limits: freePlanData.limits || FALLBACK_LIMITS,
              features: freePlanData.features || [],
              isActive: true,
            });
          } else {
            // Si no existe en Firestore, usar fallback
            setPlanInfo({
              id: 'free',
              name: 'Free',
              limits: FALLBACK_LIMITS,
              features: [],
              isActive: true,
            });
          }
        } catch {
          // Error leyendo - usar fallback
          setPlanInfo({
            id: 'free',
            name: 'Free',
            limits: FALLBACK_LIMITS,
            features: [],
            isActive: true,
          });
        }
        setLoading(false);
        return;
      }

      try {
        // Detectar trial a nivel de familia: durante el trial aplicar límites de Familiar
        let familyTrialActive = false;
        try {
          const famDoc = await getDoc(doc(db, 'families', activeFamily.id));
          const famData: any = famDoc.exists() ? famDoc.data() : null;
          const toDate = (v: any): Date | null => {
            if (!v) return null;
            try {
              return typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
            } catch {
              return null;
            }
          };

          const storedTrialEnd = toDate(famData?.trialEndsAt || famData?.trialEndAt || famData?.trialEnd);
          const start = toDate(famData?.trialStartAt || famData?.createdAt);
          const effEnd: Date | null =
            storedTrialEnd || (start && !isNaN(start.getTime())
              ? new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000)
              : null);

          if (effEnd && !isNaN(effEnd.getTime())) {
            familyTrialActive = effEnd.getTime() > Date.now();
          }
        } catch {
          familyTrialActive = false;
        }

        setIsTrialActiveState(familyTrialActive);

        // Buscar el subscription_account del owner
        let accountDoc: any = null;
        let accountPermissionDenied = false;
        
        try {
          accountDoc = await getDoc(doc(db, 'subscription_accounts', activeFamily.ownerId));
        } catch (err: any) {
          
          if (err?.code === 'permission-denied' || String(err?.message || '').includes('insufficient permissions')) {
            accountPermissionDenied = true;
          }
          if (!accountPermissionDenied) throw err;
        }

        if (accountPermissionDenied) {
          // Fallback: si no podemos leer subscription_accounts, intentamos leer el plan del owner desde users/{ownerId}
          // (esto evita que miembros/roles sin permisos caigan a Free erróneamente)
          try {
            const ownerUserDoc = await getDoc(doc(db, 'users', activeFamily.ownerId));
            const ownerPlanRaw = ownerUserDoc.exists() ? (ownerUserDoc.data() as any)?.subscription?.plan : null;
            const ownerPlanId = normalizePlanId(ownerPlanRaw || 'free');
            const effectivePlanId = familyTrialActive && ownerPlanId === 'free' ? 'familiar' : ownerPlanId;

            const planDoc = await getDoc(doc(db, 'subscriptionPlans', effectivePlanId));
            if (planDoc.exists()) {
              const planData = planDoc.data();
              setPlanInfo({
                id: planDoc.id,
                name: planData.name || 'Unknown',
                limits: planData.limits || FALLBACK_LIMITS,
                features: planData.features || [],
                isActive: planData.isActive !== false,
              });
            } else {
              setPlanInfo({
                id: effectivePlanId,
                name: effectivePlanId === 'free' ? 'Free' : 'Unknown',
                limits: FALLBACK_LIMITS_BY_PLAN[effectivePlanId] || FALLBACK_LIMITS,
                features: [],
                isActive: true,
              });
            }
          } catch {
            setPlanInfo({
              id: 'free',
              name: 'Free',
              limits: FALLBACK_LIMITS,
              features: [],
              isActive: true,
            });
          }
          return;
        }

        if (accountDoc && accountDoc.exists()) {
          const accountData = accountDoc.data();
          const rawPlanId = accountData.plan || 'free';
          const normalizedRawPlanId = normalizePlanId(rawPlanId);
          const planId = familyTrialActive && normalizedRawPlanId === 'free' ? 'familiar' : normalizedRawPlanId;
          
          // Obtener info completa del plan
          let planDoc: any = null;
          
          try {
            planDoc = await getDoc(doc(db, 'subscriptionPlans', planId));
          } catch (err: any) {
            
            if (err?.code === 'permission-denied') {
              // Sin permisos para leer subscriptionPlans - usar limits de account o fallback
              const fallbackPlanLimits = FALLBACK_LIMITS_BY_PLAN[planId] || FALLBACK_LIMITS;
              const finalPlan = {
                id: planId,
                name: planId === 'free' ? 'Free' : 'Unknown',
                limits: (accountData.limits || fallbackPlanLimits),
                features: [],
                isActive: true,
              };
              setPlanInfo(finalPlan);
              return;
            }
            throw err;
          }
          
          if (planDoc && planDoc.exists()) {
            const planData = planDoc.data();
            
            // SIEMPRE usar límites de Firestore (subscriptionPlans) - AdminPlans es la fuente de verdad
            const finalLimits = planData.limits || accountData.limits || FALLBACK_LIMITS;
            
            const finalPlan = {
              id: planDoc.id,
              name: planData.name || 'Unknown',
              limits: finalLimits,
              features: planData.features || [],
              isActive: planData.isActive !== false,
            };
            
            setPlanInfo(finalPlan);
          } else {
            // Plan no encontrado en subscriptionPlans, usar limits de account o fallback
            setPlanInfo({
              id: planId,
              name: planId === 'free' ? 'Free' : 'Unknown',
              limits: (accountData.limits || FALLBACK_LIMITS),
              features: [],
              isActive: true,
            });
          }
        } else {
          // No tiene subscription account - intentar leer plan FREE/Familiar desde Firestore
          try {
            const effectiveId = familyTrialActive ? 'familiar' : 'free';
            let freePlanDoc = await getDoc(doc(db, 'subscriptionPlans', effectiveId));
            if (!freePlanDoc.exists() && familyTrialActive) {
              freePlanDoc = await getDoc(doc(db, 'subscriptionPlans', 'family'));
            }

            if (freePlanDoc.exists()) {
              const freePlanData = freePlanDoc.data();
              setPlanInfo({
                id: effectiveId,
                name: freePlanData.name || (familyTrialActive ? 'Familiar (Prueba)' : 'Free'),
                limits: freePlanData.limits || (familyTrialActive ? FALLBACK_LIMITS_BY_PLAN.familiar : FALLBACK_LIMITS),
                features: freePlanData.features || [],
                isActive: true,
              });
            } else {
              setPlanInfo({
                id: familyTrialActive ? 'familiar' : 'free',
                name: familyTrialActive ? 'Familiar (Prueba)' : 'Free',
                limits: familyTrialActive ? FALLBACK_LIMITS_BY_PLAN.familiar : FALLBACK_LIMITS,
                features: [],
                isActive: true,
              });
            }
          } catch {
            setPlanInfo({
              id: familyTrialActive ? 'familiar' : 'free',
              name: familyTrialActive ? 'Familiar (Prueba)' : 'Free',
              limits: familyTrialActive ? FALLBACK_LIMITS_BY_PLAN.familiar : FALLBACK_LIMITS,
              features: [],
              isActive: true,
            });
          }
        }
      } catch (error) {
        // Cualquier otro error no previsto: usar fallback genérico
        setPlanInfo({
          id: 'free',
          name: 'Free',
          limits: FALLBACK_LIMITS as PlanLimits,
          features: [],
          isActive: true,
        });
      } finally {
        setLoading(false);
      }
    }

    loadPlan();
  }, [activeFamily?.id, activeFamily?.ownerId, activeFamilyLoading]);

  /**
   * Verifica si una feature está bloqueada
   * @param limitKey - Key del límite a verificar
   * @returns true si está bloqueado (-1), false si está disponible
   */
  const isFeatureBlocked = useCallback((limitKey: keyof PlanLimits): boolean => {
    if (loading || activeFamilyLoading) return false;
    if (!planInfo?.limits) return false;
    const limit = planInfo.limits[limitKey];
    return Number(limit) === -1;
  }, [planInfo?.limits, loading, activeFamilyLoading]);

  /**
   * Verifica si una feature está disponible (no bloqueada)
   */
  const isFeatureAvailable = useCallback((limitKey: keyof PlanLimits): boolean => {
    return !isFeatureBlocked(limitKey);
  }, [isFeatureBlocked]);

  /**
   * Verifica si un límite se ha alcanzado
   * @param limitKey - Key del límite
   * @param currentCount - Cantidad actual de uso
   * @returns true si el límite se alcanzó
   */
  const isLimitReached = useCallback((limitKey: keyof PlanLimits, currentCount: number): boolean => {
    if (!planInfo?.limits) return false;
    const rawLimit = planInfo.limits[limitKey];

    // undefined = sin límite configurado
    if (rawLimit === undefined) return false;

    const limit = Number(rawLimit);

    // -1 = bloqueado
    if (limit === -1) return true;

    // 0 = ilimitado
    if (limit === 0) return false;

    // Comparar con el límite
    return currentCount >= limit;
  }, [planInfo?.limits]);

  /**
   * Obtiene el límite de una feature
   * @returns -1 si está bloqueado, 0 si es ilimitado, o el número específico
   */
  const getLimit = useCallback((limitKey: keyof PlanLimits): number => {
    if (!planInfo?.limits) return -1;
    const limit = planInfo.limits[limitKey];
    return limit !== undefined ? Number(limit) : -1;
  }, [planInfo?.limits]);

  /**
   * Verifica si el plan actual es Free
   */
  const isFree = planInfo?.id === 'free';

  /**
   * Verifica si el plan actual es de pago
   */
  const isPaid = planInfo && planInfo.id !== 'free';

  return useMemo(() => ({
    planInfo,
    loading,
    isTrialActive: isTrialActiveState,
    isFeatureBlocked,
    isFeatureAvailable,
    isLimitReached,
    getLimit,
    isFree,
    isPaid,
  }), [
    planInfo,
    loading,
    isTrialActiveState,
    isFeatureBlocked,
    isFeatureAvailable,
    isLimitReached,
    getLimit,
    isFree,
    isPaid,
  ]);
}
